import React, { useState, useEffect } from 'react';
import { Organization, OrgFeatureFlags, OrgParentPolicies, OrgStaffPolicies } from '../types';
import {
  MODULE_DEFINITIONS,
  STAFF_POLICY_DEFINITIONS,
  PARENT_POLICY_DEFINITIONS,
  FEATURE_PRESETS,
  testGeminiApiKey
} from '../lib/features';
import { saveOrgApiKey, getOrgApiKey } from '../firebaseUtils';

interface OrgModulesModalProps {
  org: Organization;
  isOpen: boolean;
  onClose: () => void;
  onSave: (orgId: string, updates: Partial<Organization>) => Promise<void>;
  mode?: 'super_admin' | 'principal';
}

export const OrgModulesModal: React.FC<OrgModulesModalProps> = ({
  org,
  isOpen,
  onClose,
  onSave,
  mode = 'super_admin'
}) => {
  const [activeTab, setActiveTab] = useState<'modules' | 'staff_policies' | 'parent_policies' | 'byok'>(
    mode === 'principal' ? 'staff_policies' : 'modules'
  );
  
  // Local state for edits
  const [featureFlags, setFeatureFlags] = useState<OrgFeatureFlags>({
    homework: org.featureFlags?.homework !== false,
    attendance: org.featureFlags?.attendance !== false,
    chat: org.featureFlags?.chat !== false,
    announcements: org.featureFlags?.announcements !== false,
    calendar: org.featureFlags?.calendar !== false,
    assessments: org.featureFlags?.assessments !== false,
    kudos: org.featureFlags?.kudos !== false,
    aiTutor: org.featureFlags?.aiTutor !== false,
    weeklyCheckin: org.featureFlags?.weeklyCheckin !== false,
    familyConnect: org.featureFlags?.familyConnect !== false,
    insights: org.featureFlags?.insights !== false
  });

  const [staffPolicies, setStaffPolicies] = useState<OrgStaffPolicies>({
    enableTeacherInsights: org.staffPolicies?.enableTeacherInsights !== false,
    enableTeacherAssessments: org.staffPolicies?.enableTeacherAssessments !== false,
    enableTeacherKudos: org.staffPolicies?.enableTeacherKudos !== false,
    enableTeacherChat: org.staffPolicies?.enableTeacherChat !== false,
    enableTeacherAttendanceCalendar: org.staffPolicies?.enableTeacherAttendanceCalendar !== false,
    enableTeacherHomework: org.staffPolicies?.enableTeacherHomework !== false,
    enableTeacherAiHomeworkHelper: org.staffPolicies?.enableTeacherAiHomeworkHelper !== false,
    allowTeacherInviteCodeAudit: org.staffPolicies?.allowTeacherInviteCodeAudit !== false
  });

  const [parentPolicies, setParentPolicies] = useState<OrgParentPolicies>({
    allowParentInitiatedChat: org.parentPolicies?.allowParentInitiatedChat !== false,
    showLeaderboardOrPublicKudos: org.parentPolicies?.showLeaderboardOrPublicKudos !== false,
    allowParentReportAbsence: org.parentPolicies?.allowParentReportAbsence !== false,
    enableAiTutorForStudents: org.parentPolicies?.enableAiTutorForStudents !== false,
    enableWeeklyCheckin: org.parentPolicies?.enableWeeklyCheckin !== false,
    enableFamilyConnect: org.parentPolicies?.enableFamilyConnect !== false,
    enableAssessmentGradebook: org.parentPolicies?.enableAssessmentGradebook !== false
  });

  const [customApiKey, setCustomApiKey] = useState<string>(org.customApiKey || '');
  const [showApiKey, setShowApiKey] = useState<boolean>(false);
  const [testStatus, setTestStatus] = useState<{ testing: boolean; message?: string; success?: boolean } | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [activePresetId, setActivePresetId] = useState<string | null>(null);

  useEffect(() => {
    if (org) {
      setFeatureFlags({
        homework: org.featureFlags?.homework !== false,
        attendance: org.featureFlags?.attendance !== false,
        chat: org.featureFlags?.chat !== false,
        announcements: org.featureFlags?.announcements !== false,
        calendar: org.featureFlags?.calendar !== false,
        assessments: org.featureFlags?.assessments !== false,
        kudos: org.featureFlags?.kudos !== false,
        aiTutor: org.featureFlags?.aiTutor !== false,
        weeklyCheckin: org.featureFlags?.weeklyCheckin !== false,
        familyConnect: org.featureFlags?.familyConnect !== false,
        insights: org.featureFlags?.insights !== false
      });

      setStaffPolicies({
        enableTeacherInsights: org.staffPolicies?.enableTeacherInsights !== false,
        enableTeacherAssessments: org.staffPolicies?.enableTeacherAssessments !== false,
        enableTeacherKudos: org.staffPolicies?.enableTeacherKudos !== false,
        enableTeacherChat: org.staffPolicies?.enableTeacherChat !== false,
        enableTeacherAttendanceCalendar: org.staffPolicies?.enableTeacherAttendanceCalendar !== false,
        enableTeacherHomework: org.staffPolicies?.enableTeacherHomework !== false,
        enableTeacherAiHomeworkHelper: org.staffPolicies?.enableTeacherAiHomeworkHelper !== false,
        allowTeacherInviteCodeAudit: org.staffPolicies?.allowTeacherInviteCodeAudit !== false
      });

      setParentPolicies({
        allowParentInitiatedChat: org.parentPolicies?.allowParentInitiatedChat !== false,
        showLeaderboardOrPublicKudos: org.parentPolicies?.showLeaderboardOrPublicKudos !== false,
        allowParentReportAbsence: org.parentPolicies?.allowParentReportAbsence !== false,
        enableAiTutorForStudents: org.parentPolicies?.enableAiTutorForStudents !== false,
        enableWeeklyCheckin: org.parentPolicies?.enableWeeklyCheckin !== false,
        enableFamilyConnect: org.parentPolicies?.enableFamilyConnect !== false,
        enableAssessmentGradebook: org.parentPolicies?.enableAssessmentGradebook !== false
      });

      setCustomApiKey(org.customApiKey || '');
      setTestStatus(null);
      setActiveTab(mode === 'principal' ? 'staff_policies' : 'modules');

      // Attempt to load from secure subcollection if available
      getOrgApiKey(org.id).then(secureKey => {
        if (secureKey) {
          setCustomApiKey(secureKey);
        }
      }).catch(e => {
        console.warn('Could not load secure API key:', e);
      });
    }
  }, [org, isOpen, mode]);

  if (!isOpen) return null;

  const handleApplyPreset = (preset: typeof FEATURE_PRESETS[0]) => {
    setActivePresetId(preset.id);
    if (mode === 'super_admin') {
      setFeatureFlags({ ...preset.featureFlags });
    }
    setStaffPolicies({ ...preset.staffPolicies });
    setParentPolicies({ ...preset.parentPolicies });
  };

  const handleToggleFeature = (key: keyof OrgFeatureFlags) => {
    if (mode !== 'super_admin') return;
    setActivePresetId(null);
    setFeatureFlags(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleToggleStaffPolicy = (key: keyof OrgStaffPolicies) => {
    setActivePresetId(null);
    setStaffPolicies(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleToggleParentPolicy = (key: keyof OrgParentPolicies) => {
    setActivePresetId(null);
    setParentPolicies(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleTestKey = async () => {
    if (!customApiKey.trim()) {
      setTestStatus({ testing: false, success: false, message: 'Please enter an API Key to test.' });
      return;
    }
    setTestStatus({ testing: true, message: 'Verifying API key with Google AI...' });
    const result = await testGeminiApiKey(customApiKey.trim());
    setTestStatus({ testing: false, success: result.success, message: result.message });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const trimmedKey = customApiKey.trim();
      
      // Save securely into isolated subcollection (accessible only by admins)
      await saveOrgApiKey(org.id, trimmedKey || undefined);

      await onSave(org.id, {
        featureFlags: mode === 'super_admin' ? featureFlags : org.featureFlags,
        staffPolicies,
        parentPolicies,
        hasCustomApiKey: Boolean(trimmedKey),
        customApiKey: trimmedKey || undefined, // Keep for backward compatibility while secure subcollection takes precedence
        updatedAt: new Date()
      });
      onClose();
    } catch (e: any) {
      alert(`Error saving configuration: ${e.message || e}`);
    } finally {
      setIsSaving(false);
    }
  };

  const categories = [
    { id: 'core', title: 'Core Academic Essentials', icon: 'school' },
    { id: 'communication', title: 'Direct Communication & Schedule', icon: 'forum' },
    { id: 'academics', title: 'Assessments & Grading', icon: 'fact_check' },
    { id: 'ai', title: 'AI & Intelligence Tools', icon: 'psychology' },
    { id: 'wellbeing', title: 'Wellbeing, Kudos & Community', icon: 'favorite' }
  ];

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 z-50 animate-fade-in">
      <div className="bg-white rounded-3xl max-w-3xl w-full shadow-2xl border border-slate-100 max-h-[90vh] flex flex-col overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-bold text-lg shadow-sm">
              <span className="material-icons-round text-xl">tune</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-extrabold text-slate-900 text-base sm:text-lg">
                  {mode === 'super_admin' ? 'Feature & Policy Configuration' : 'School Features & Policies Controls'}
                </h3>
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 text-[10px] font-bold uppercase rounded-md">
                  {mode === 'super_admin' ? 'Platform Super Admin' : 'Principal Hub'}
                </span>
              </div>
              <p className="text-xs text-slate-500 font-medium truncate max-w-md">
                Managing rollouts & policies for <strong>{org.name}</strong>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-200/70 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-colors cursor-pointer"
          >
            <span className="material-icons-round text-lg">close</span>
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="px-6 border-b border-slate-200 bg-white flex gap-1 sm:gap-2 pt-2 overflow-x-auto no-scrollbar">
          {mode === 'super_admin' && (
            <button
              onClick={() => setActiveTab('modules')}
              className={`px-3.5 py-2.5 text-xs font-bold border-b-2 flex items-center gap-1.5 whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === 'modules'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span className="material-icons-round text-sm">widgets</span>
              <span>Global Modules</span>
            </button>
          )}

          <button
            onClick={() => setActiveTab('staff_policies')}
            className={`px-3.5 py-2.5 text-xs font-bold border-b-2 flex items-center gap-1.5 whitespace-nowrap transition-colors cursor-pointer ${
              activeTab === 'staff_policies'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <span className="material-icons-round text-sm">badge</span>
            <span>Staff & Teachers</span>
          </button>
          
          <button
            onClick={() => setActiveTab('parent_policies')}
            className={`px-3.5 py-2.5 text-xs font-bold border-b-2 flex items-center gap-1.5 whitespace-nowrap transition-colors cursor-pointer ${
              activeTab === 'parent_policies'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <span className="material-icons-round text-sm">family_restroom</span>
            <span>Parents & Students</span>
          </button>

          {mode === 'principal' && (
            <button
              onClick={() => setActiveTab('modules')}
              className={`px-3.5 py-2.5 text-xs font-bold border-b-2 flex items-center gap-1.5 whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === 'modules'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span className="material-icons-round text-sm">view_in_ar</span>
              <span>Admin Modules Status</span>
            </button>
          )}

          <button
            onClick={() => setActiveTab('byok')}
            className={`px-3.5 py-2.5 text-xs font-bold border-b-2 flex items-center gap-1.5 whitespace-nowrap transition-colors cursor-pointer ${
              activeTab === 'byok'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <span className="material-icons-round text-sm">key</span>
            <span>AI Key (BYOK)</span>
            {customApiKey && <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />}
          </button>
        </div>

        {/* Scrollable Content Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
          
          {/* TAB 1: GLOBAL MODULES (Editable for Super Admin, Overview for Principal) */}
          {activeTab === 'modules' && (
            <div className="space-y-6">
              {/* Presets Bar */}
              <div className="bg-indigo-50/70 p-4 rounded-2xl border border-indigo-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-extrabold text-indigo-950 uppercase tracking-wider flex items-center gap-1.5">
                    <span className="material-icons-round text-sm text-indigo-600">auto_fix_high</span>
                    Quick Setup Archetype Packs
                  </span>
                  <span className="text-[11px] text-indigo-700 font-medium">1-Click presets</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                  {FEATURE_PRESETS.map(preset => {
                    const isSelected = activePresetId === preset.id;
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => handleApplyPreset(preset)}
                        className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                            : 'bg-white text-slate-800 border-indigo-100 hover:border-indigo-300 hover:bg-indigo-50/50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`material-icons-round text-base ${isSelected ? 'text-white' : 'text-indigo-600'}`}>
                            {preset.icon}
                          </span>
                          <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded uppercase ${
                            isSelected ? 'bg-indigo-700 text-white' : 'bg-indigo-100 text-indigo-800'
                          }`}>
                            {preset.badge}
                          </span>
                        </div>
                        <p className={`text-xs font-bold leading-tight ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                          {preset.name}
                        </p>
                        <p className={`text-[10px] line-clamp-2 mt-0.5 ${isSelected ? 'text-indigo-100' : 'text-slate-500'}`}>
                          {preset.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {mode === 'principal' && (
                <div className="p-4 bg-indigo-50/90 rounded-2xl border border-indigo-200 text-indigo-950 text-xs flex items-start gap-3">
                  <span className="material-icons-round text-indigo-600 text-lg flex-shrink-0 mt-0.5">verified_user</span>
                  <div className="space-y-1">
                    <p className="font-bold text-indigo-900">Platform License Status</p>
                    <p className="text-indigo-800/90 leading-relaxed">
                      These core modules are licensed and entitled by the Super Admin. You can customize which features are active or inactive for your <strong>School Staff</strong> and <strong>Parents</strong> using the tabs above.
                    </p>
                  </div>
                </div>
              )}

              {/* Module Categories */}
              {categories.map(cat => {
                const modulesInCat = MODULE_DEFINITIONS.filter(m => m.category === cat.id);
                if (modulesInCat.length === 0) return null;

                return (
                  <div key={cat.id} className="space-y-3">
                    <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                      <span className="material-icons-round text-indigo-600 text-sm">{cat.icon}</span>
                      <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">{cat.title}</h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {modulesInCat.map(mod => {
                        const isEnabled = featureFlags[mod.key] !== false;
                        const isSuper = mode === 'super_admin';

                        return (
                          <div
                            key={mod.key}
                            className={`p-3.5 rounded-2xl border transition-all flex items-start justify-between gap-3 ${
                              isEnabled
                                ? 'bg-white border-slate-200 shadow-2xs'
                                : 'bg-slate-50/70 border-slate-150 opacity-75'
                            }`}
                          >
                            <div className="flex items-start gap-3 min-w-0">
                              <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                isEnabled ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-500'
                              }`}>
                                <span className="material-icons-round text-base">{mod.icon}</span>
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs font-bold text-slate-900">{mod.label}</span>
                                  {mod.aiPowered && (
                                    <span className="px-1.5 py-0.2 bg-purple-100 text-purple-700 text-[9px] font-extrabold rounded">
                                      AI
                                    </span>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-500 leading-snug mt-0.5 line-clamp-2">
                                  {mod.description}
                                </p>
                              </div>
                            </div>

                            {/* Toggle Switch */}
                            {isSuper ? (
                              <button
                                type="button"
                                onClick={() => handleToggleFeature(mod.key)}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                  isEnabled ? 'bg-indigo-600' : 'bg-slate-300'
                                }`}
                              >
                                <span
                                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                    isEnabled ? 'translate-x-5' : 'translate-x-0'
                                  }`}
                                />
                              </button>
                            ) : (
                              <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md uppercase flex-shrink-0 ${
                                isEnabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'
                              }`}>
                                {isEnabled ? 'Entitled' : 'Inactive'}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* TAB 2: STAFF & TEACHER CONTROLS */}
          {activeTab === 'staff_policies' && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                <div className="flex items-center gap-2 text-slate-900 font-bold text-xs mb-1">
                  <span className="material-icons-round text-base text-indigo-600">badge</span>
                  Staff & Teacher Capabilities & Tool Controls
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Decide which tools and features are active for your school teachers. For instance, if your school is not ready to roll out <strong>AI Classroom Insights</strong> or wants to stage <strong>Assessments & Gradebooks</strong>, you can deactivate them here without affecting other tools.
                </p>
              </div>

              <div className="space-y-3">
                {STAFF_POLICY_DEFINITIONS.map(policy => {
                  const isFeatureEntitled = featureFlags[policy.associatedFeature] !== false;
                  const isStaffActive = staffPolicies[policy.key] !== false;
                  const isDisabledBySuperAdmin = !isFeatureEntitled;

                  return (
                    <div
                      key={policy.key}
                      className={`p-4 rounded-2xl border transition-all flex items-start justify-between gap-4 ${
                        isDisabledBySuperAdmin
                          ? 'bg-slate-100/70 border-slate-200 opacity-60'
                          : isStaffActive
                          ? 'bg-white border-indigo-100 shadow-2xs'
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isDisabledBySuperAdmin 
                            ? 'bg-slate-200 text-slate-400'
                            : isStaffActive
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-slate-200 text-slate-500'
                        }`}>
                          <span className="material-icons-round text-lg">{policy.icon}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-bold text-slate-900">{policy.label}</span>
                            {policy.aiPowered && (
                              <span className="px-1.5 py-0.2 bg-purple-100 text-purple-700 text-[9px] font-extrabold rounded">
                                AI
                              </span>
                            )}
                            {isDisabledBySuperAdmin ? (
                              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-semibold rounded flex items-center gap-1">
                                <span className="material-icons-round text-[12px]">lock</span>
                                Disabled by Super Admin
                              </span>
                            ) : isStaffActive ? (
                              <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded">
                                Active for Staff
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-slate-200 text-slate-600 text-[10px] font-semibold rounded">
                                Inactive / Hidden
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                            {policy.description}
                          </p>
                          {isDisabledBySuperAdmin && (
                            <p className="text-[11px] text-amber-700 mt-1 font-medium">
                              To activate this tool, the associated module must first be enabled by your Platform Super Admin.
                            </p>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        disabled={isDisabledBySuperAdmin}
                        onClick={() => handleToggleStaffPolicy(policy.key)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          isDisabledBySuperAdmin
                            ? 'bg-slate-200 cursor-not-allowed'
                            : isStaffActive
                            ? 'bg-indigo-600'
                            : 'bg-slate-300'
                        }`}
                        title={isDisabledBySuperAdmin ? 'Disabled at Platform level' : isStaffActive ? 'Deactivate for staff' : 'Activate for staff'}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            isStaffActive && !isDisabledBySuperAdmin ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 3: PARENT EXPERIENCE & STUDENT CONTROLS */}
          {activeTab === 'parent_policies' && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                <div className="flex items-center gap-2 text-slate-900 font-bold text-xs mb-1">
                  <span className="material-icons-round text-base text-indigo-600">family_restroom</span>
                  Parent & Student Experience Controls
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Control how parents and students interact with school features. Enable self-service online absence reports and AI tutors, or customize communication boundaries.
                </p>
              </div>

              <div className="space-y-3">
                {PARENT_POLICY_DEFINITIONS.map(policy => {
                  const isFeatureEntitled = featureFlags[policy.associatedFeature] !== false;
                  const isPolicyActive = parentPolicies[policy.key] !== false;
                  const isDisabledBySuperAdmin = !isFeatureEntitled;

                  return (
                    <div
                      key={policy.key}
                      className={`p-4 rounded-2xl border transition-all flex items-start justify-between gap-4 ${
                        isDisabledBySuperAdmin
                          ? 'bg-slate-100/70 border-slate-200 opacity-60'
                          : isPolicyActive
                          ? 'bg-white border-indigo-100 shadow-2xs'
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isDisabledBySuperAdmin 
                            ? 'bg-slate-200 text-slate-400'
                            : isPolicyActive
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-slate-200 text-slate-500'
                        }`}>
                          <span className="material-icons-round text-lg">{policy.icon}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-bold text-slate-900">{policy.label}</span>
                            {policy.aiPowered && (
                              <span className="px-1.5 py-0.2 bg-purple-100 text-purple-700 text-[9px] font-extrabold rounded">
                                AI
                              </span>
                            )}
                            {isDisabledBySuperAdmin ? (
                              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-semibold rounded flex items-center gap-1">
                                <span className="material-icons-round text-[12px]">lock</span>
                                Disabled by Super Admin
                              </span>
                            ) : isPolicyActive ? (
                              <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded">
                                Active for Parents
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-slate-200 text-slate-600 text-[10px] font-semibold rounded">
                                Inactive / Hidden
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                            {policy.description}
                          </p>
                          {isDisabledBySuperAdmin && (
                            <p className="text-[11px] text-amber-700 mt-1 font-medium">
                              To activate this for parents, the module must first be enabled by your Platform Super Admin.
                            </p>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        disabled={isDisabledBySuperAdmin}
                        onClick={() => handleToggleParentPolicy(policy.key)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          isDisabledBySuperAdmin
                            ? 'bg-slate-200 cursor-not-allowed'
                            : isPolicyActive
                            ? 'bg-indigo-600'
                            : 'bg-slate-300'
                        }`}
                        title={isDisabledBySuperAdmin ? 'Disabled at Platform level' : isPolicyActive ? 'Deactivate for parents' : 'Activate for parents'}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            isPolicyActive && !isDisabledBySuperAdmin ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 4: BYOK (BRING YOUR OWN KEY) */}
          {activeTab === 'byok' && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-2xl border border-purple-100">
                <div className="flex items-center gap-2 text-purple-900 font-bold text-xs mb-1">
                  <span className="material-icons-round text-base text-purple-600">psychology</span>
                  Bring Your Own Gemini API Key (BYOK)
                </div>
                <p className="text-xs text-purple-800 leading-relaxed">
                  Offload AI generation expenses (AI Tutor hints, multilingual translation, weekly insights) to the school's direct Google AI Studio or Vertex AI account. If left empty, requests automatically use the platform default.
                </p>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">
                    School Gemini API Key (Optional)
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={customApiKey}
                      onChange={e => {
                        setCustomApiKey(e.target.value);
                        setTestStatus(null);
                      }}
                      placeholder="AIzaSy..."
                      className="w-full p-3 pr-20 border border-slate-300 rounded-xl text-xs font-mono bg-white text-slate-800 focus:border-indigo-600 focus:outline-none"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="p-1.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                        title={showApiKey ? 'Hide key' : 'Show key'}
                      >
                        <span className="material-icons-round text-base">
                          {showApiKey ? 'visibility_off' : 'visibility'}
                        </span>
                      </button>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Get a free API key from <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">Google AI Studio</a>.
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleTestKey}
                    disabled={testStatus?.testing || !customApiKey.trim()}
                    className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 font-bold rounded-xl text-xs flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                  >
                    <span className="material-icons-round text-sm">
                      {testStatus?.testing ? 'hourglass_top' : 'check_circle'}
                    </span>
                    <span>{testStatus?.testing ? 'Testing Key...' : 'Test API Key'}</span>
                  </button>

                  {customApiKey && (
                    <button
                      type="button"
                      onClick={() => {
                        setCustomApiKey('');
                        setTestStatus(null);
                      }}
                      className="px-3 py-2 text-slate-500 hover:text-red-600 text-xs font-semibold cursor-pointer"
                    >
                      Clear / Reset to System Default
                    </button>
                  )}
                </div>

                {testStatus && (
                  <div className={`p-3 rounded-xl border text-xs flex items-center gap-2 ${
                    testStatus.testing
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-800'
                      : testStatus.success
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : 'bg-red-50 border-red-200 text-red-800'
                  }`}>
                    <span className="material-icons-round text-base">
                      {testStatus.testing ? 'sync' : testStatus.success ? 'verified' : 'error'}
                    </span>
                    <span>{testStatus.message}</span>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 font-semibold rounded-xl text-xs cursor-pointer"
          >
            Cancel
          </button>
          
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs shadow-xs flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
          >
            <span className="material-icons-round text-sm">save</span>
            <span>{isSaving ? 'Saving Changes...' : 'Save Configuration'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
